// The spotlight and the floating lens: "make sure we can see what you are referring to when you are talking about it".
// show(focus) dims the page, scrolls the target to the middle, outlines it, and opens ONE overlay beside the dock holding
//   - a static clone of a station card / tank card (ids stripped, data-lens-of set, kept live by ui.renderInto), or
//   - a live second trend chart (createTrendLens) for `lane:<id>` targets.
// hide() undoes all of it. Placement is CSS (index.html .lens): left of the dock panel on desktop, above the sheet bar on a phone.
import { PHONE_QUERY } from "./dock.js"
import { createTrendLens } from "./chart.js"

const REDUCED = "(prefers-reduced-motion: reduce)"

/**
 * @param doc          document
 * @param ui           createUI() result: `renderInto(root, unitOrTank)` keeps a clone live
 * @param history      createHistory() instance (for trend lenses)
 * @param detected     the detector's events array (read live by trend lenses)
 * @param trendFactory (root, opts) -> { destroy() }; defaults to createTrendLens
 */
// Hidden = the `hidden` attribute on the target or an ancestor, or a computed display:none (jsdom-safe: no layout query).
function isHidden(el) {
  if (!el) return true
  if (el.closest && el.closest("[hidden]")) return true
  const w = el.ownerDocument && el.ownerDocument.defaultView
  try { return !!(w && w.getComputedStyle(el).display === "none") } catch (_) { return false }
}

export function createLens({ doc, ui, history, detected = [], trendFactory = createTrendLens } = {}) {
  if (!doc) throw new TypeError("createLens: `doc` is required")
  if (!ui || typeof ui.renderInto !== "function") throw new TypeError("createLens: `ui` (with renderInto) is required; without it a clone would silently freeze")
  if (!history || typeof history.queryMany !== "function") throw new TypeError("createLens: `history` (with queryMany) is required for trend lenses")
  const win = doc.defaultView
  const media = (q) => (win && win.matchMedia ? win.matchMedia(q).matches : false)
  const st = { box: null, body: null, title: null, clone: null, parts: [], trend: null, target: null, drag: null, spot: false, mo: null }
  const subs = { esc: null, out: null }

  const h = (tag, cls, text) => { const e = doc.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e }

  function build() {
    const box = h("div", "lens"); box.setAttribute("role", "dialog"); box.setAttribute("aria-label", "Magnified view")
    const head = h("div", "lens-head")
    const title = h("span", "lens-title")
    const close = h("button", "lens-close", "×"); close.type = "button"; close.setAttribute("aria-label", "Close the magnified view")
    close.addEventListener("click", () => lens.hide())
    head.append(title, close)
    const body = h("div", "lens-body")
    box.append(head, body)
    head.addEventListener("pointerdown", (e) => startDrag(e, close))
    doc.body.appendChild(box)
    st.box = box; st.body = body; st.title = title
  }

  /** Width of the docked panel while it is open on desktop (0 otherwise): the lens may never be dragged over it. */
  function dockWidth() {
    if (!doc.body.classList.contains("dock-panel-open")) return 0
    return parseFloat(win.getComputedStyle(doc.documentElement).getPropertyValue("--dock-w")) || parseFloat(doc.documentElement.style.getPropertyValue("--dock-w")) || 380
  }
  /** Pin the box at (left, top), clamped to the viewport and, while the dock panel is open, to its left. */
  function place(left, top) {
    const w = st.box.offsetWidth, hh = st.box.offsetHeight
    const L = Math.min(Math.max(0, left), Math.max(0, win.innerWidth - dockWidth() - w)), T = Math.min(Math.max(0, top), Math.max(0, win.innerHeight - hh))
    const s = st.box.style; s.left = `${L}px`; s.top = `${T}px`; s.right = "auto"; s.bottom = "auto"
  }
  /** A dragged lens is re-clamped when the dock opens, closes or resizes (the body class changes). */
  function reclamp() {
    if (!st.box || !st.box.style.left || st.box.style.left === "auto") return
    place(parseFloat(st.box.style.left) || 0, parseFloat(st.box.style.top) || 0)
  }

  // ---- drag by the header (desktop only; the phone lens is pinned above the sheet) ----
  function startDrag(e, close) {
    if (media(PHONE_QUERY) || e.target === close || (e.button != null && e.button !== 0)) return
    const r = st.box.getBoundingClientRect()
    st.drag = { dx: e.clientX - r.left, dy: e.clientY - r.top }
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) { /* jsdom / synthetic events */ }
    const move = (ev) => {
      if (!st.drag || !st.box) return
      place(ev.clientX - st.drag.dx, ev.clientY - st.drag.dy)
    }
    const up = () => { st.drag = null; doc.removeEventListener("pointermove", move); doc.removeEventListener("pointerup", up); doc.removeEventListener("pointercancel", up) }
    doc.addEventListener("pointermove", move); doc.addEventListener("pointerup", up); doc.addEventListener("pointercancel", up)
  }

  // ---- targets ----
  function resolve(target) {
    if (typeof target !== "string") return null
    if (target.startsWith("lane:")) {
      const id = target.slice(5)
      return { lane: id, el: doc.querySelector(`#trendRoot [data-lane="${id}"]`) || doc.querySelector(`[data-lane="${id}"]`) }
    }
    const el = doc.getElementById(target)
    return el ? { el } : null
  }

  /** Static clone: every id becomes data-lens-of, tab stops and highlight state are dropped, the copy is inert. */
  function makeClone(el) {
    const c = el.cloneNode(true)
    for (const n of [c, ...c.querySelectorAll("[id]")]) if (n.id) { n.setAttribute("data-lens-of", n.id); n.removeAttribute("id") }
    for (const n of [c, ...c.querySelectorAll("[tabindex]")]) n.removeAttribute("tabindex")
    for (const n of [c, ...c.querySelectorAll(".tour-highlight")]) n.classList.remove("tour-highlight")
    c.setAttribute("aria-hidden", "true"); c.setAttribute("data-lens-clone", ""); c.inert = true
    return c
  }
  /** The widgets inside a clone that ui.renderInto can keep live: [element, key]. */
  function liveParts(clone) {
    const parts = []
    for (const n of [clone, ...clone.querySelectorAll("[data-unit], [data-lens-of='holdingTankCard']")]) {
      const key = n.dataset && n.dataset.unit ? n.dataset.unit : n.getAttribute("data-lens-of") === "holdingTankCard" ? "TK-200" : null
      if (!key) continue
      // Looked up once here: the per-frame update touches the DOM only through this map.
      const nodes = new Map()
      for (const e of [n, ...n.querySelectorAll("[data-lens-of]")]) nodes.set(e.getAttribute("data-lens-of"), e)
      parts.push([n, key, nodes])
    }
    return parts
  }
  function update() {
    if (!st.clone) return
    for (const [n, key, nodes] of st.parts) ui.renderInto(n, key, nodes)
  }

  function clearSpotlight() {
    if (st.target) st.target.style.scrollMarginBottom = ""
    if (doc.body) doc.body.classList.remove("tour-dim")
    for (const e of doc.querySelectorAll(".tour-highlight")) e.classList.remove("tour-highlight")
  }
  function clearContent() {
    if (st.trend) { st.trend.destroy(); st.trend = null }
    st.clone = null; st.parts = []
    if (st.body) st.body.textContent = ""
    if (st.box) { st.box.classList.remove("lens-trend", "lens-clone") }
  }

  function arm() {
    if (subs.esc) return
    // Capture on window, ahead of the dock's and the Operator pane's own Escape handlers: the first Escape closes the
    // lens only. A visible modal (welcome, Operator pane) owns Escape, as it does for the dock.
    subs.esc = (e) => {
      if (e.key !== "Escape" || modalOpen()) return
      e.preventDefault(); e.stopImmediatePropagation(); lens.hide()
    }
    subs.out = (e) => { if (media(PHONE_QUERY) && st.box && !st.box.contains(e.target) && !(e.target && e.target.closest && e.target.closest("#dock"))) lens.hide() }
    win.addEventListener("keydown", subs.esc, true); doc.addEventListener("pointerdown", subs.out)
    if (typeof win.MutationObserver === "function") { st.mo = new win.MutationObserver(reclamp); st.mo.observe(doc.body, { attributes: true, attributeFilter: ["class"] }) }
  }
  function disarm() {
    if (subs.esc) win.removeEventListener("keydown", subs.esc, true)
    if (subs.out) doc.removeEventListener("pointerdown", subs.out)
    if (st.mo) { st.mo.disconnect(); st.mo = null }
    subs.esc = subs.out = null
  }
  const modalOpen = () => Array.from(doc.querySelectorAll('[aria-modal="true"]')).some((m) => !m.hidden && !m.closest("[hidden]") && (!m.classList.contains("fp-backdrop") || m.classList.contains("open")))

  const lens = {
    /** focus = { target: elementId | 'lane:<id>', zoom?, window?, emphasize?, lanes?, spot? } */
    show(focus = {}) {
      let hit = resolve(focus.target)
      if (!hit || (!hit.el && !hit.lane)) { lens.hide(); return }
      // A hidden target (the alarm banner with nothing active) would clone to an empty box over a dimmed page.
      if (hit.el && !hit.lane && isHidden(hit.el)) { lens.hide(); return }
      // The whole trend panel is a stack of canvases: its lens is the trend lens, over the window the page is showing.
      if (hit.el && hit.el.id === "trendPanel") hit = { lane: "wt", el: hit.el }
      // A clone would be a blank canvas; `spot` asks for the spotlight alone (a target wider than any lens, e.g. the station grid).
      const spotOnly = !hit.lane && (focus.spot === true || hit.el.tagName === "CANVAS" || !!hit.el.querySelector("canvas"))
      clearSpotlight()
      clearContent()
      if (spotOnly) { if (st.box) { st.box.remove(); st.box = st.body = st.title = null } } else if (!st.box) build()
      st.target = hit.el || null; st.spot = spotOnly

      if (hit.lane) {
        const lanes = (focus.lanes && focus.lanes.length ? focus.lanes : [hit.lane]).slice(0, 2)
        const chip = doc.querySelector("#windowChips .chip.active[data-window]")
        const windowS = focus.window || (chip && Number(chip.dataset.window)) || 900
        st.box.classList.add("lens-trend")
        const root = h("div", "lens-trend-root"); st.body.appendChild(root)
        st.trend = trendFactory(root, { history, detected, lanes, window: windowS, emphasize: focus.emphasize || null })
        const pageTitle = doc.querySelector(`#trendRoot [data-lane="${lanes[0]}"] .trend-lane-title b`)
        st.title.textContent = (pageTitle ? pageTitle.textContent : `Trend · ${lanes.join(" + ")}`) + (focus.emphasize ? ` · ${focus.emphasize}` : "")
      } else if (!spotOnly) {
        const rect = hit.el.getBoundingClientRect()
        const natW = Math.round(rect.width), natH = Math.round(rect.height)
        const clone = makeClone(hit.el), phone = media(PHONE_QUERY)
        const availW = phone ? (win.innerWidth || 390) - 40 : Math.min(560, 0.42 * (win.innerWidth || 1440)) - 24
        const availH = phone ? 0.45 * (win.innerHeight || 844) - 64 : (win.innerHeight || 900) - 110
        const want = focus.zoom || 1.5
        // A phone lens still magnifies (>= 1.2): the body scrolls if the card outgrows it.
        const zoom = natW ? Math.max(phone ? Math.min(1.2, want) : 1, Math.min(want, availW / natW, natH ? availH / natH : Infinity)) : 1
        const wrap = h("div", "lens-clone-wrap")
        if (natW) { wrap.style.width = `${Math.round(natW * zoom)}px`; wrap.style.height = `${Math.round(natH * zoom)}px`; clone.style.width = `${natW}px`; clone.style.transform = `scale(${zoom})` }
        wrap.appendChild(clone); st.body.appendChild(wrap)
        st.box.classList.add("lens-clone")
        st.clone = clone; st.parts = liveParts(clone)
        st.title.textContent = (hit.el.getAttribute("aria-label") || hit.el.id || "View").replace(/, open operator pane$/, "")
        update()
      }

      doc.body.classList.add("tour-dim")
      if (hit.el) {
        hit.el.classList.add("tour-highlight")
        // Scrolled to the middle, a target that sits in the lens's columns must stay clear of it; one beside it needs no room
        // (reserving it would push a left-hand card up under the sticky header).
        if (st.box) {
          const t = hit.el.getBoundingClientRect(), l = st.box.getBoundingClientRect()
          if (t.left < l.right && t.right > l.left) hit.el.style.scrollMarginBottom = `${st.box.offsetHeight + 24}px`
        }
        if (typeof hit.el.scrollIntoView === "function") hit.el.scrollIntoView({ block: "center", behavior: media(REDUCED) ? "auto" : "smooth" })
      }
      arm()
    },
    hide() {
      clearContent()
      if (st.box) { st.box.remove(); st.box = st.body = st.title = null }
      clearSpotlight()
      st.target = null; st.drag = null; st.spot = false
      disarm()
    },
    isOpen() { return !!st.box || st.spot },
    /** Re-render the clone from the live model; the host calls this once per frame while open. */
    update,
    destroy() { lens.hide() },
  }
  return lens
}
